import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import React from 'react';
import { Modal, Pressable, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface AppointmentActionsPopupProps {
    visible: boolean;
    onClose: () => void;
    onReschedule: () => void;
    onCancelRequest: () => void;
}

/**
 * Temporary workaround for one mistagged Contentful ADHA entry: its `key`
 * field is the fully-qualified "propertyDetails.cancelAppointmentRequest",
 * but the `type` field has landed on the wrong namespace in Contentful (seen
 * as both "propertyDetails" and "properties"), so it nests one level off
 * from where t() actually reads. Check the known wrong shapes directly
 * before falling back to the normal t() lookup.
 * Remove once the Contentful entry's `type`/`key` fields are corrected.
 */
function resolveCancelAppointmentRequestLabel(
    t: ReturnType<typeof useTranslation>['t'],
    getTranslations: ReturnType<typeof useTranslation>['getTranslations'],
): string {
    const adha = (getTranslations() as unknown as { adha?: Record<string, any> })?.adha;
    const candidates = [
        adha?.propertyDetails?.propertyDetails?.cancelAppointmentRequest,
        adha?.properties?.propertyDetails?.cancelAppointmentRequest,
    ];
    const found = candidates.find((value) => typeof value === 'string' && value.length > 0);
    return found ?? t('propertyDetails.cancelAppointmentRequest');
}

export const AppointmentActionsPopup: React.FC<AppointmentActionsPopupProps> = ({
    visible,
    onClose,
    onReschedule,
    onCancelRequest,
}) => {
    const colors = useUIColors();
    const { t, getTranslations } = useTranslation();
    const insets = useSafeAreaInsets();
    const cancelAppointmentRequestLabel = resolveCancelAppointmentRequestLabel(t, getTranslations);

    return (
        <Modal
            visible={visible}
            transparent
            animationType="slide"
            onRequestClose={onClose}
        >
            <Pressable style={styles.overlay} onPress={onClose}>
                <Pressable 
                    style={[
                        styles.container, 
                        { 
                            backgroundColor: colors.backgroundLevel1 || '#FFFFFF',
                            paddingBottom: Math.max(insets.bottom, 24) + 12
                        }
                    ]}
                    onPress={(e) => e.stopPropagation()}
                >
                    <View
                        style={[
                            styles.dragHandle,
                            { backgroundColor: colors.outlineInactive },
                        ]}
                    />

                    <Button
                        title={t('propertyDetails.reschedule')}
                        variant="primary"
                        onPress={() => {
                            trackCatalogEvent({
                                screen: ALMETLAA_SCREEN.APPOINTMENT,
                                actionName: ALMETLAA_ACTION.APPOINTMENT.SLOT_DIALOG_OPEN,
                                eventType: 'action',
                            });
                            onReschedule();
                        }}
                        fullWidth
                        style={[styles.rescheduleButton, { backgroundColor: colors.buttonDefault || '#1A1A1A' }]}
                        textStyle={styles.rescheduleText}
                        testID="appointment-actions-reschedule-button"
                    />
                    
                    <TouchableOpacity
                        onPress={() => {
                            trackCatalogEvent({
                                screen: ALMETLAA_SCREEN.APPOINTMENT,
                                actionName: ALMETLAA_ACTION.APPOINTMENT.CANCEL_REQUEST_CLICK,
                                eventType: 'action',
                            });
                            onCancelRequest();
                        }}
                        style={styles.cancelButton}
                        activeOpacity={0.7}
                        testID="appointment-actions-cancel-button"
                    >
                        <Typography 
                            variant="body" 
                            style={[styles.cancelText, { color: colors.fontPrimary || '#1A1A1A' }]}
                        >
                            {cancelAppointmentRequestLabel}
                        </Typography>
                    </TouchableOpacity>
                </Pressable>
            </Pressable>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: UI_PRIMITIVES.overlayBackdrop || 'rgba(0, 0, 0, 0.4)',
        justifyContent: 'flex-end',
    },
    container: {
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingTop: 28,
        paddingHorizontal: 20,
        alignItems: 'center',
        gap: 16,
        width: '100%',
    },
    dragHandle: {
        width: 60,
        height: 4,
        borderRadius: 2,
        alignSelf: 'center',
        marginBottom: 12,
    },
    rescheduleButton: {
        height: 52,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    rescheduleText: {
        fontSize: 16,
        fontWeight: '600',
    },
    cancelButton: {
        paddingVertical: 12,
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cancelText: {
        fontSize: 16,
        fontWeight: '600',
        textDecorationLine: 'underline',
    },
});

export default AppointmentActionsPopup;
